import { Command } from 'commander'
import chalk from 'chalk'
import path from 'path'
import fs from 'fs-extra'
import yaml from 'js-yaml'
import { exec } from 'child_process'
import { promisify } from 'util'
// Table import will be added when needed for detailed display
import { formatBytes, formatTimeDiff } from '../utils/format.js'

const execAsync = promisify(exec)

export function createStatsCommand(): Command {
  const stats = new Command('stats')
    .alias('st')
    .description('Display project statistics and analytics')
    .option('-d, --detail', 'Show detailed information')
    .option('-j, --json', 'Output in JSON format')
    .option('-e, --export <format>', 'Export report (csv/html)')
    .option('-t, --trend', 'Show task completion trend')
    .action(async (options) => {
      const workflowDir = path.join(process.cwd(), '.workflow')
      
      if (!await fs.pathExists(workflowDir)) {
        console.log(chalk.red('❌ Current directory is not a workflow project'))
        console.log(chalk.gray('Run "workflow init" to initialize'))
        return
      }
      
      // Collect statistics
      const statistics = await collectStats(workflowDir)
      
      if (options.json) {
        console.log(JSON.stringify(statistics, null, 2))
        return
      }
      
      // Handle export
      if (options.export) {
        await exportStats(statistics, options.export)
        return
      }
      
      // Show trend
      if (options.trend) {
        await displayTrend(workflowDir)
        return
      }
      
      // Display statistics
      displayStats(statistics, options.detail)
    })
  
  return stats
}

interface ProjectStats {
  project: {
    name: string
    created: Date
    lastModified: Date
    size: number
  }
  tasks: {
    total: number
    byStatus: Record<string, number>
    byType: Record<string, number>
    byOwner: Record<string, number>
    byPriority: Record<string, number>
    completionRate: number
    averageTime: number
    overdueCount: number
  }
  prds: {
    total: number
    totalSize: number
  }
  docs: {
    total: number
    byType: Record<string, number>
    byStatus: Record<string, number>
    totalSize: number
  }
  git?: {
    branch: string
    commits: number
    lastCommit: Date
    contributors: number
  }
  workflow: {
    version: string
    lastSync: Date | null
    ideIntegrations: string[]
  }
}

async function collectStats(workflowDir: string): Promise<ProjectStats> {
  const stats: ProjectStats = {
    project: {
      name: path.basename(process.cwd()),
      created: new Date(),
      lastModified: new Date(),
      size: 0
    },
    tasks: {
      total: 0,
      byStatus: {},
      byType: {},
      byOwner: {},
      byPriority: {},
      completionRate: 0,
      averageTime: 0,
      overdueCount: 0
    },
    prds: {
      total: 0,
      totalSize: 0
    },
    docs: {
      total: 0,
      byType: {},
      byStatus: {},
      totalSize: 0
    },
    workflow: {
      version: '0.2.6',
      lastSync: null,
      ideIntegrations: []
    }
  }
  
  // Project info
  const workflowStat = await fs.stat(workflowDir)
  stats.project.created = workflowStat.birthtime
  stats.project.lastModified = workflowStat.mtime
  
  // Task statistics
  const taskDir = path.join(workflowDir, 'tasks')
  if (await fs.pathExists(taskDir)) {
    const taskFiles = await fs.readdir(taskDir)
    stats.tasks.total = taskFiles.filter(f => f.endsWith('.json')).length
    
    for (const file of taskFiles) {
      if (!file.endsWith('.json')) continue
      
      const content = await fs.readFile(path.join(taskDir, file), 'utf-8')
      const task = JSON.parse(content)
      
      // By status
      const status = task.status || 'pending'
      stats.tasks.byStatus[status] = (stats.tasks.byStatus[status] || 0) + 1
      
      // By type
      const type = task.type || 'task'
      stats.tasks.byType[type] = (stats.tasks.byType[type] || 0) + 1
      
      // By owner
      if (task.assignee || task.owner) {
        const owner = task.assignee || task.owner
        stats.tasks.byOwner[owner] = (stats.tasks.byOwner[owner] || 0) + 1
      }
      
      // By priority
      if (task.priority) {
        stats.tasks.byPriority[task.priority] = (stats.tasks.byPriority[task.priority] || 0) + 1
      }
      
      // Calculate completion rate
      if ((task.status === 'done' || task.status === 'completed') && stats.tasks.total > 0) {
        const completedCount = (stats.tasks.byStatus['done'] || 0) + (stats.tasks.byStatus['completed'] || 0)
        stats.tasks.completionRate = completedCount / stats.tasks.total * 100
      }
      
      // Check overdue tasks
      if (task.dueDate && new Date(task.dueDate) < new Date() && 
          task.status !== 'done' && task.status !== 'completed') {
        stats.tasks.overdueCount++
      }
    }
  }
  
  // PRD statistics
  const prdDir = path.join(workflowDir, 'prds')
  if (await fs.pathExists(prdDir)) {
    const prdFiles = await fs.readdir(prdDir)
    stats.prds.total = prdFiles.filter(f => f.endsWith('.md')).length
    
    for (const file of prdFiles) {
      if (!file.endsWith('.md')) continue
      const fileStat = await fs.stat(path.join(prdDir, file))
      stats.prds.totalSize += fileStat.size
    }
  }
  
  // Document statistics
  const docsDir = path.join(workflowDir, 'docs')
  if (await fs.pathExists(docsDir)) {
    const docFiles = await fs.readdir(docsDir)
    stats.docs.total = docFiles.filter(f => f.endsWith('.md')).length
    
    for (const file of docFiles) {
      if (!file.endsWith('.md')) continue
      
      const filePath = path.join(docsDir, file)
      const fileStat = await fs.stat(filePath)
      stats.docs.totalSize += fileStat.size
      
      const content = await fs.readFile(filePath, 'utf-8')
      const match = content.match(/^---\n([\s\S]*?)\n---/)
      
      if (match) {
        try {
          const metadata = yaml.load(match[1]) as any
          
          // By type
          if (metadata.type) {
            stats.docs.byType[metadata.type] = (stats.docs.byType[metadata.type] || 0) + 1
          }
          
          // By status
          if (metadata.status) {
            stats.docs.byStatus[metadata.status] = (stats.docs.byStatus[metadata.status] || 0) + 1
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    }
  }
  
  // Git statistics
  try {
    const { stdout: branch } = await execAsync('git branch --show-current')
    stats.git = {
      branch: branch.trim(),
      commits: 0,
      lastCommit: new Date(),
      contributors: 0
    }
    
    const { stdout: commitCount } = await execAsync('git rev-list --count HEAD')
    stats.git.commits = parseInt(commitCount.trim())
    
    const { stdout: lastCommitDate } = await execAsync('git log -1 --format=%cd')
    stats.git.lastCommit = new Date(lastCommitDate.trim())
    
    const { stdout: contributors } = await execAsync('git log --format="%an" | sort -u | wc -l')
    stats.git.contributors = parseInt(contributors.trim())
  } catch (e) {
    // Git not available
  }
  
  // IDE integrations
  if (await fs.pathExists(path.join(process.cwd(), '.cursor'))) {
    stats.workflow.ideIntegrations.push('Cursor')
  }
  if (await fs.pathExists(path.join(process.cwd(), 'CLAUDE.md'))) {
    stats.workflow.ideIntegrations.push('Claude')
  }
  if (await fs.pathExists(path.join(process.cwd(), '.vscode'))) {
    stats.workflow.ideIntegrations.push('VS Code')
  }
  
  // Last sync time
  const syncLog = path.join(workflowDir, '.sync.log')
  if (await fs.pathExists(syncLog)) {
    const syncStat = await fs.stat(syncLog)
    stats.workflow.lastSync = syncStat.mtime
  }
  
  // Calculate total size
  stats.project.size = await calculateDirSize(workflowDir)
  
  return stats
}

async function calculateDirSize(dir: string): Promise<number> {
  let size = 0
  
  const files = await fs.readdir(dir)
  for (const file of files) {
    const filePath = path.join(dir, file)
    const stat = await fs.stat(filePath)
    
    if (stat.isDirectory()) {
      size += await calculateDirSize(filePath)
    } else {
      size += stat.size
    }
  }
  
  return size
}

function displayStats(stats: ProjectStats, detail: boolean) {
  console.log(chalk.blue.bold('\n📊 Workflow Project Statistics\n'))
  
  // Project overview
  console.log(chalk.white.bold('📁 Project Information'))
  console.log(chalk.gray('  Name: ') + chalk.cyan(stats.project.name))
  console.log(chalk.gray('  Created: ') + formatTimeDiff(stats.project.created) + chalk.gray(` (${stats.project.created.toLocaleDateString()})`))
  console.log(chalk.gray('  Last Modified: ') + formatTimeDiff(stats.project.lastModified))
  console.log(chalk.gray('  Workflow Directory Size: ') + chalk.yellow(formatBytes(stats.project.size)))
  console.log()
  
  // Task statistics
  if (stats.tasks.total > 0) {
    console.log(chalk.white.bold('📋 Task Statistics'))
    console.log(chalk.gray('  Total: ') + chalk.cyan(stats.tasks.total))
    
    if (Object.keys(stats.tasks.byStatus).length > 0) {
      console.log(chalk.gray('  Status Distribution:'))
      for (const [status, count] of Object.entries(stats.tasks.byStatus)) {
        const icon = getStatusIcon(status)
        const percent = Math.round((count / stats.tasks.total) * 100)
        console.log(chalk.gray(`    ${icon} ${status}: `) + chalk.green(count) + chalk.gray(` (${percent}%)`))
      }
    }
    
    if (stats.tasks.overdueCount > 0) {
      console.log(chalk.red(`  ⚠️  Overdue Tasks: ${stats.tasks.overdueCount}`))
    }
    
    if (detail && Object.keys(stats.tasks.byType).length > 0) {
      console.log(chalk.gray('  Type Distribution:'))
      for (const [type, count] of Object.entries(stats.tasks.byType)) {
        console.log(chalk.gray(`    • ${type}: `) + chalk.green(count))
      }
    }
    
    if (detail && Object.keys(stats.tasks.byOwner).length > 0) {
      console.log(chalk.gray('  Assignees:'))
      for (const [owner, count] of Object.entries(stats.tasks.byOwner)) {
        console.log(chalk.gray(`    • ${owner}: `) + chalk.green(count))
      }
    }
    
    if (detail && Object.keys(stats.tasks.byPriority).length > 0) {
      console.log(chalk.gray('  Priority Distribution:'))
      for (const [priority, count] of Object.entries(stats.tasks.byPriority)) {
        const icon = getPriorityIcon(priority)
        console.log(chalk.gray(`    ${icon} ${priority}: `) + chalk.green(count))
      }
    }
    console.log()
  }
  
  // PRD statistics
  if (stats.prds.total > 0) {
    console.log(chalk.white.bold('📑 PRD Statistics'))
    console.log(chalk.gray('  Total: ') + chalk.cyan(stats.prds.total))
    console.log(chalk.gray('  Total Size: ') + chalk.yellow(formatBytes(stats.prds.totalSize)))
    console.log(chalk.gray('  Average Size: ') + chalk.yellow(formatBytes(stats.prds.totalSize / stats.prds.total)))
    console.log()
  }
  
  // Document statistics
  if (stats.docs.total > 0) {
    console.log(chalk.white.bold('📚 Document Statistics'))
    console.log(chalk.gray('  Total: ') + chalk.cyan(stats.docs.total))
    console.log(chalk.gray('  Total Size: ') + chalk.yellow(formatBytes(stats.docs.totalSize)))
    
    if (Object.keys(stats.docs.byType).length > 0) {
      console.log(chalk.gray('  Type Distribution:'))
      for (const [type, count] of Object.entries(stats.docs.byType)) {
        console.log(chalk.gray(`    • ${type}: `) + chalk.green(count))
      }
    }
    
    if (Object.keys(stats.docs.byStatus).length > 0) {
      console.log(chalk.gray('  Status Distribution:'))
      for (const [status, count] of Object.entries(stats.docs.byStatus)) {
        const icon = getDocStatusIcon(status)
        console.log(chalk.gray(`    ${icon} ${status}: `) + chalk.green(count))
      }
    }
    console.log()
  }
  
  // Git statistics
  if (stats.git) {
    console.log(chalk.white.bold('🔀 Git Information'))
    console.log(chalk.gray('  Current Branch: ') + chalk.cyan(stats.git.branch))
    console.log(chalk.gray('  Commits: ') + chalk.green(stats.git.commits))
    console.log(chalk.gray('  Contributors: ') + chalk.green(stats.git.contributors))
    console.log(chalk.gray('  Last Commit: ') + formatTimeDiff(stats.git.lastCommit))
    console.log()
  }
  
  // Workflow information
  console.log(chalk.white.bold('🔧 Workflow Information'))
  console.log(chalk.gray('  Version: ') + chalk.cyan(stats.workflow.version))
  console.log(chalk.gray('  IDE Integrations: ') + (stats.workflow.ideIntegrations.length > 0 ? 
    stats.workflow.ideIntegrations.map(ide => chalk.green(ide)).join(', ') : 
    chalk.gray('None')))
  console.log(chalk.gray('  Last Sync: ') + (stats.workflow.lastSync ? 
    formatTimeDiff(stats.workflow.lastSync) : 
    chalk.gray('Never synced')))
}

function getStatusIcon(status: string): string {
  const icons: Record<string, string> = {
    pending: '📝',
    'in-progress': '🔄',
    'in_progress': '🔄',
    review: '👀',
    done: '✅',
    completed: '✅',
    cancelled: '❌',
    blocked: '🚫'
  }
  return icons[status] || '•'
}

function getPriorityIcon(priority: string): string {
  const icons: Record<string, string> = {
    urgent: '🔴',
    high: '🟠',
    medium: '🟡',
    low: '🟢'
  }
  return icons[priority] || '•'
}

function getDocStatusIcon(status: string): string {
  const icons: Record<string, string> = {
    draft: '📝',
    review: '👀',
    published: '✅',
    archived: '📦'
  }
  return icons[status] || '•'
}

async function exportStats(stats: ProjectStats, format: string) {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  const filename = `workflow-report-${timestamp}.${format}`
  
  switch (format) {
    case 'csv':
      await exportCSV(stats, filename)
      break
    case 'html':
      await exportHTML(stats, filename)
      break
    case 'pdf':
      console.log(chalk.yellow('🚧 PDF export is under development'))
      break
    default:
      console.log(chalk.red(`✖ Unsupported export format: ${format}`))
      return
  }
  
  console.log(chalk.green(`\n✅ Report exported to: ${filename}`))
}

async function exportCSV(stats: ProjectStats, filename: string) {
  const lines = [
    'Workflow Project Statistics Report',
    `Generated,${new Date().toLocaleString()}`,
    '',
    'Project Information',
    `Project Name,${stats.project.name}`,
    `Created,${stats.project.created.toLocaleDateString()}`,
    `Size,${formatBytes(stats.project.size)}`,
    '',
    'Task Statistics',
    `Total,${stats.tasks.total}`,
    `Completion Rate,${stats.tasks.completionRate.toFixed(2)}%`,
    `Overdue Tasks,${stats.tasks.overdueCount}`,
    '',
    'Status Distribution',
    'Status,Count',
    ...Object.entries(stats.tasks.byStatus).map(([status, count]) => `${status},${count}`),
    '',
    'Type Distribution',
    'Type,Count',
    ...Object.entries(stats.tasks.byType).map(([type, count]) => `${type},${count}`),
    '',
    'Assignee Distribution',
    'Assignee,Task Count',
    ...Object.entries(stats.tasks.byOwner).map(([owner, count]) => `${owner},${count}`)
  ]
  
  await fs.writeFile(filename, lines.join('\n'), 'utf-8')
}

async function exportHTML(stats: ProjectStats, filename: string) {
  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Workflow Project Report</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 20px; background: #f5f5f5; }
    .container { max-width: 1000px; margin: 0 auto; background: white; padding: 30px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
    h1 { color: #333; border-bottom: 2px solid #4fc3f7; padding-bottom: 10px; }
    h2 { color: #666; margin-top: 30px; }
    .stat-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin: 20px 0; }
    .stat-card { background: #f8f8f8; padding: 20px; border-radius: 6px; text-align: center; }
    .stat-value { font-size: 32px; font-weight: bold; color: #4fc3f7; }
    .stat-label { color: #666; margin-top: 5px; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    th, td { padding: 10px; text-align: left; border-bottom: 1px solid #eee; }
    th { background: #f8f8f8; font-weight: 600; }
    .chart { margin: 20px 0; }
  </style>
</head>
<body>
  <div class="container">
    <h1>📊 Workflow Project Statistics Report</h1>
    <p>Generated: ${new Date().toLocaleString()}</p>
    
    <h2>Project Overview</h2>
    <div class="stat-grid">
      <div class="stat-card">
        <div class="stat-value">${stats.tasks.total}</div>
        <div class="stat-label">Total Tasks</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${stats.tasks.completionRate.toFixed(1)}%</div>
        <div class="stat-label">Completion Rate</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${stats.tasks.overdueCount}</div>
        <div class="stat-label">Overdue Tasks</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${Object.keys(stats.tasks.byOwner).length}</div>
        <div class="stat-label">Team Members</div>
      </div>
    </div>
    
    <h2>Task Status Distribution</h2>
    <table>
      <tr><th>Status</th><th>Count</th><th>Percentage</th></tr>
      ${Object.entries(stats.tasks.byStatus).map(([status, count]) => 
        `<tr><td>${getStatusIcon(status)} ${status}</td><td>${count}</td><td>${(count/stats.tasks.total*100).toFixed(1)}%</td></tr>`
      ).join('')}
    </table>
    
    ${Object.keys(stats.tasks.byOwner).length > 0 ? `
    <h2>Team Workload</h2>
    <table>
      <tr><th>Assignee</th><th>Tasks</th><th>Percentage</th></tr>
      ${Object.entries(stats.tasks.byOwner).map(([owner, count]) => 
        `<tr><td>${owner}</td><td>${count}</td><td>${(count/stats.tasks.total*100).toFixed(1)}%</td></tr>`
      ).join('')}
    </table>
    ` : ''}
    
    ${stats.git ? `
    <h2>Git Statistics</h2>
    <div class="stat-grid">
      <div class="stat-card">
        <div class="stat-value">${stats.git.commits}</div>
        <div class="stat-label">Total Commits</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${stats.git.contributors}</div>
        <div class="stat-label">Contributors</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${stats.git.branch}</div>
        <div class="stat-label">Current Branch</div>
      </div>
    </div>
    ` : ''}
  </div>
</body>
</html>`
  
  await fs.writeFile(filename, html, 'utf-8')
}

async function displayTrend(workflowDir: string) {
  console.log(chalk.blue.bold('\n📈 Task Completion Trend\n'))
  
  // Read all task files
  const taskDir = path.join(workflowDir, 'tasks')
  const taskFiles = await fs.readdir(taskDir)
  const tasks = []
  
  for (const file of taskFiles) {
    if (!file.endsWith('.json')) continue
    const content = await fs.readFile(path.join(taskDir, file), 'utf-8')
    const task = JSON.parse(content)
    tasks.push(task)
  }
  
  // Group by week
  const weeklyStats = new Map()
  const now = new Date()
  
  for (let i = 0; i < 8; i++) {
    const weekStart = new Date(now)
    weekStart.setDate(weekStart.getDate() - (i * 7))
    weekStart.setHours(0, 0, 0, 0)
    
    const weekEnd = new Date(weekStart)
    weekEnd.setDate(weekEnd.getDate() + 7)
    
    const weekTasks = tasks.filter(task => {
      const created = new Date(task.createdAt || task.created)
      return created >= weekStart && created < weekEnd
    })
    
    const completed = weekTasks.filter(t => t.status === 'done' || t.status === 'completed').length
    const total = weekTasks.length
    
    if (total > 0) {
      const weekKey = `${weekStart.getMonth() + 1}/${weekStart.getDate()}`
      weeklyStats.set(weekKey, { total, completed, rate: (completed / total * 100) })
    }
  }
  
  // Display trend
  console.log(chalk.gray('Recent 8 weeks task completion:'))
  console.log()
  
  const weeks = Array.from(weeklyStats.entries()).reverse()
  for (const [week, stats] of weeks) {
    const bar = '█'.repeat(Math.floor(stats.rate / 5))
    console.log(
      chalk.gray(`  ${week.padEnd(6)} `) +
      chalk.green(bar.padEnd(20)) +
      chalk.gray(` ${stats.rate.toFixed(1)}% `) +
      chalk.gray(`(${stats.completed}/${stats.total})`)
    )
  }
  
  console.log()
  console.log(chalk.gray('Legend: █ = 5%'))
}